import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, UserIcon, BuildingIcon, FileTextIcon } from 'lucide-react';
import { Sale } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { TransactionFinancialBreakdown } from '../../../shared/ui/TransactionFinancialBreakdown';
import { SCENARIOS, buildScenario } from '../../payments/utils/transactionScenarios';
import { getMockSaleById, getSaleFixture } from '../../../shared/mocks/mockSales';
import { computeTransactionViewModel } from '../../payments/utils/transactionViewModel';
import { formatMoney } from '../../../shared/lib/money';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { getAvailableBalance } from '../../refunds/utils/refundFunding';

/** Dev-only balance scenarios, so every funding outcome can be demonstrated. */
type BalanceScenario = 'live' | 'covers' | 'partial' | 'zero' | 'negative';
const BALANCE_SCENARIOS: {
  id: BalanceScenario;
  label: string;
}[] = [{
  id: 'live',
  label: 'Balance: live'
}, {
  id: 'covers',
  label: 'Balance: covers refund'
}, {
  id: 'partial',
  label: 'Balance: partially covers'
}, {
  id: 'zero',
  label: 'Balance: zero'
}, {
  id: 'negative',
  label: 'Balance: already negative'
}];

/**
 * Dev-only acquirer negative balance limits. "None" is the same rule as
 * "the acquirer does not allow a negative balance" — it's a limit of zero.
 */
type LimitScenario = 'none' | 'tight' | 'ample';
const LIMIT_SCENARIOS: {
  id: LimitScenario;
  label: string;
}[] = [{
  id: 'none',
  label: 'Negative limit: none'
}, {
  id: 'tight',
  label: 'Negative limit: tight'
}, {
  id: 'ample',
  label: 'Negative limit: ample'
}];
export function SaleDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const {
    selectedCompany
  } = useCompany();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [balanceScenario, setBalanceScenario] = useState<BalanceScenario>('live');
  const [limitScenario, setLimitScenario] = useState<LimitScenario>('none');
  // Get sale fixture and initialize scenario from it
  const saleFixture = getSaleFixture(id || 'payment-with-reserve');
  const [selectedScenarioId, setSelectedScenarioId] = useState(saleFixture?.defaultScenarioId || 'payment-with-reserve');
  // Get base sale data from shared fixtures
  const baseSale = getMockSaleById(id || 'payment-with-reserve');
  // Compute current scenario data (updates when scenario changes)
  const currentScenario = useMemo(() => {
    return buildScenario(selectedScenarioId);
  }, [selectedScenarioId]);
  const currentViewModel = useMemo(() => {
    return computeTransactionViewModel(currentScenario.transaction, currentScenario.ledgerRows, 'included');
  }, [currentScenario]);
  // Build sale object with current scenario data
  const sale: Sale = useMemo(() => {
    if (!baseSale) {
      // Fallback if sale not found
      return {
        id: id || 'unknown',
        transactionId: currentScenario.transaction.transactionId,
        amount: currentViewModel.headerAmount,
        currency: currentScenario.transaction.currency,
        status: 'completed',
        source: 'payment_link',
        date: currentScenario.transaction.date,
        customerName: 'Unknown Customer',
        customerEmail: 'unknown@example.com',
        paymentMethod: {
          type: 'card'
        },
        merchantName: 'Unknown Merchant',
        merchantId: 'MERCH-000',
        description: currentScenario.description,
        fee: currentViewModel.summary.feesPostedDisplay,
        netAmount: currentViewModel.summary.netPosted,
        lifecycle: []
      };
    }
    // Override transaction data with current scenario
    return {
      ...baseSale,
      transactionId: currentScenario.transaction.transactionId,
      amount: currentViewModel.headerAmount,
      currency: currentScenario.transaction.currency,
      date: currentScenario.transaction.date,
      fee: currentViewModel.summary.feesPostedDisplay,
      netAmount: currentViewModel.summary.netPosted
    };
  }, [baseSale, currentScenario, currentViewModel, id]);
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      refund_initiated: 'bg-emerald-100 text-emerald-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-12569" data-mp="2zw4fktwihlc3">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  // Refunds draw down the merchant's available balance, so the modal needs
  // the same number Balance summary shows. The scenarios below only override
  // it for demonstration.
  const liveAvailableBalance = getAvailableBalance(selectedCompany);
  const availableBalance = (() => {
    switch (balanceScenario) {
      case 'covers':
        return sale.amount * 4;
      case 'partial':
        return Math.round(sale.amount * 0.4 * 100) / 100;
      case 'zero':
        return 0;
      case 'negative':
        return Math.round(sale.amount * -0.5 * 100) / 100;
      default:
        return liveAvailableBalance;
    }
  })();
  const acquirerPolicy = {
    negativeBalanceLimit: limitScenario === 'ample' ? Math.round(sale.amount * 2 * 100) / 100 : limitScenario === 'tight' ? Math.round(sale.amount * 0.3 * 100) / 100 : 0
  };
  const handleRefund = (amount: number, reason: string, notes: string) => {
    const shortfall = Math.max(0, amount - Math.max(availableBalance, 0));
    console.log('Refund initiated:', {
      transactionId: sale.transactionId,
      amount,
      reason,
      notes,
      availableBalance,
      shortfall
    });
    // In a real app, this would call an API to process the refund
    alert(shortfall > 0 ? `Refund of ${formatMoney(amount, sale.currency)} initiated. ${formatMoney(shortfall, sale.currency)} is not covered by your available balance and will be recovered from your next payouts.` : `Refund of ${formatMoney(amount, sale.currency)} initiated successfully!`);
  };
  return <div className="space-y-6" data-id="element-12570" data-mp="1mrth39k12f99">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-12571" data-mp="1ipgayk1dcfby">
        <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-12572" data-mp="3h413fyia9ntf">
          <ArrowLeftIcon size={20} data-id="element-12573" data-mp="3d3qxgluhqxek" />
          {t('pages.SaleDetail.back_to_sales')}
        </button>
        <div className="flex items-center gap-3" data-id="element-12574" data-mp="yu5aeqqak6ot">
          {/* Dev-only scenario selector */}
          <select value={selectedScenarioId} onChange={e => setSelectedScenarioId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-12575" data-mp="13sggqrhdnz2a">
            {SCENARIOS.map(scenario => <option key={scenario.scenarioId} value={scenario.scenarioId} data-id="element-12576" data-mp="fwu9z2sxb77s">
                {scenario.description}
              </option>)}
          </select>
          {/* Dev-only: refund funding scenarios. The balance and the
           acquirer's negative limit are independent inputs, so they're two
           controls rather than a combined list. */}
          {sale.status === 'completed' && <>
              <select value={balanceScenario} onChange={e => setBalanceScenario(e.target.value as BalanceScenario)} aria-label="Simulated available balance" className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-12577" data-mp="20be1z2xhjaab">
                {BALANCE_SCENARIOS.map(s => <option key={s.id} value={s.id} data-id="element-12578" data-mp="1psblw4cxglz8">
                    {s.label}
                  </option>)}
              </select>
              <select value={limitScenario} onChange={e => setLimitScenario(e.target.value as LimitScenario)} aria-label="Simulated acquirer negative balance limit" className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-12579" data-mp="1znln4pvi4pby">
                {LIMIT_SCENARIOS.map(s => <option key={s.id} value={s.id} data-id="element-12580" data-mp="ebtwupbntlmm">
                    {s.label}
                  </option>)}
              </select>
            </>}
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-12581" data-mp="5joiay526tq">
            <DownloadIcon size={18} data-id="element-12582" data-mp="463kvcaw16et" />
            {t('pages.SaleDetail.download_receipt')}
          </button>
          {sale.status === 'completed' && <button onClick={() => setRefundModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-12583" data-mp="3rk7htt8di8ob">
              <RefreshCwIcon size={18} data-id="element-12584" data-mp="3hm1j1khv5ino" />
              {t('pages.SaleDetail.refund')}
            </button>}
        </div>
      </div>
      {/* Transaction Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-12585" data-mp="33ov11s4yrnzi">
        <div className="flex items-start justify-between mb-6" data-id="element-12586" data-mp="38m4xvsuon1xd">
          <div data-id="element-12587" data-mp="11uvrbb7hpeox">
            <div className="text-4xl font-bold text-gray-900" data-id="element-12588" data-mp="11w6rl11q84ba">
              {formatMoney(sale.amount, sale.currency)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2" data-id="element-12589" data-mp="16s5mz8lh2ml7">
              {sale.transactionId}
            </h1>
          </div>
          <div data-id="element-12590" data-mp="3uma8gjc9m6ug">{getStatusBadge(sale.status)}</div>
        </div>

        {/* Transaction Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12591" data-mp="3guvni6ysrvkr">
          {/* Customer Information */}
          <div className="space-y-4" data-id="element-12592" data-mp="3kv5v1o28mu1q">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12593" data-mp="3kw76vur2vkki">
              <UserIcon size={20} className="text-blue-600" data-id="element-12594" data-mp="cgnh55pm8ka7" />
              {t('pages.OrderDetail.customer_information')}
            </h3>
            <div className="space-y-3" data-id="element-12595" data-mp="3ffvbn4adbx0">
              <div data-id="element-12596" data-mp="3ee7sj632wye">
                <div className="text-sm text-gray-500" data-id="element-12597" data-mp="3exojwjzzfau">
                  {t('pages.SaleDetail.name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12598" data-mp="8gdbxv9ndxwe">
                  {sale.customerName}
                </div>
              </div>
              <div data-id="element-12599" data-mp="2t51anpeha70i">
                <div className="text-sm text-gray-500" data-id="element-12600" data-mp="2t6ch5wzb5svb">
                  {t('pages.OrderDetail.email')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12601" data-mp="2kptw06iotpof">
                  {sale.customerEmail}
                </div>
              </div>
              {sale.customerPhone && <div data-id="element-12602" data-mp="2ul5cat9qv4ya">
                  <div className="text-sm text-gray-500" data-id="element-12603" data-mp="2pmu4sp6x96j8">
                    {t('pages.SaleDetail.phone')}
                  </div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-12604" data-mp="21whqmwl32dk7">
                    {sale.customerPhone}
                  </div>
                </div>}
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4" data-id="element-12605" data-mp="2bodoq7n663of">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12606" data-mp="26wm5yja9928u">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-12607" data-mp="1j6ruo65l3lqp" />
              {t('pages.OrderDetail.payment_information')}
            </h3>
            <div className="space-y-3" data-id="element-12608" data-mp="1e6vqo6wvzub5">
              <div data-id="element-12609" data-mp="1e5ue1y082zrb">
                <div className="text-sm text-gray-500" data-id="element-12610" data-mp="3ea6bpz0u8jbt">
                  {t('pages.OrderDetail.payment_method')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12611" data-mp="3j7q380mjqslp">
                  {sale.paymentMethod.brand || sale.paymentMethod.type}{' '}
                  {sale.paymentMethod.last4 && `•••• ${sale.paymentMethod.last4}`}
                  {sale.paymentMethod.details && sale.paymentMethod.details}
                </div>
              </div>
              <div data-id="element-12612" data-mp="309vod5yqlmve">
                <div className="text-sm text-gray-500" data-id="element-12613" data-mp="30b6omvsz4chr">
                  {t('pages.SaleDetail.transaction_date')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12614" data-mp="34afkq7bhkmgh">
                  {new Date(sale.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              <div data-id="element-12615" data-mp="ojb3rn2kb892">
                <div className="text-sm text-gray-500" data-id="element-12616" data-mp="oiroke4aqxyq">
                  {t('pages.SaleDetail.source')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12617" data-mp="tpg8m2e0g8t2">
                  {sale.source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>
            </div>
          </div>
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-12618" data-mp="fzmlboi5lwlg">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12619" data-mp="ayykkb44i1e1">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-12620" data-mp="b1bbvevrto3r" />
              {t('pages.SaleDetail.merchant_information')}
            </h3>
            <div className="space-y-3" data-id="element-12621" data-mp="1z22wtg5i2sh">
              <div data-id="element-12622" data-mp="203cethiqh83">
                <div className="text-sm text-gray-500" data-id="element-12623" data-mp="20mxuk6dnnqv">
                  {t('pages.SaleDetail.merchant_name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12624" data-mp="1wc1nl0ze1n6m">
                  {sale.merchantName}
                </div>
              </div>
              <div data-id="element-12625" data-mp="23gaz84bcehy2">
                <div className="text-sm text-gray-500" data-id="element-12626" data-mp="28eks289lh6gi">
                  {t('pages.SaleDetail.merchant_id')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12627" data-mp="1ufl28xacilir">
                  {sale.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Additional Details */}
          {sale.invoiceId && <div className="space-y-4" data-id="element-12628" data-mp="1kmx7e82xermo">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12629" data-mp="1pg8jooqic0lz">
                <FileTextIcon size={20} className="text-blue-600" data-id="element-12630" data-mp="1ar40b47z7qca" />
                {t('pages.SaleDetail.related_documents')}
              </h3>
              <div className="space-y-3" data-id="element-12631" data-mp="1fqgktibdklbp">
                <div data-id="element-12632" data-mp="11q5r87ioksmu">
                  <div className="text-sm text-gray-500" data-id="element-12633" data-mp="11pmct0s8omdk">
                    {t('pages.SaleDetail.invoice')}
                  </div>
                  <button onClick={() => navigate(`/invoice-detail/${sale.invoiceId}`)} className="text-sm font-medium text-blue-600 hover:underline" data-id="element-12634" data-mp="16opgohwhgmjy">
                    {sale.invoiceId}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* New Financial Breakdown */}
      <TransactionFinancialBreakdown transactionId={sale.transactionId} scenarioId={selectedScenarioId} currency={sale.currency} data-id="element-12635" data-mp="1621ifwmeritk" />

      {/* Transaction Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-12636" data-mp="1fruuszudk1yv">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-12637" data-mp="1fu782zp6to5l">
          {t('pages.SaleDetail.transaction_lifecycle')}
        </h2>
        <div className="space-y-6" data-id="element-12638" data-mp="1osj63msdvo82">
          {currentScenario.ledgerRows.filter(row => row.status === 'Posted').map((row, index, filteredRows) => <div key={row.id} className="relative" data-id="element-12639" data-mp="hga41erymv2j" data-mpi={index}>
                {index !== filteredRows.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-12640" data-mp="mf4vf53ztry9"></div>}
                <div className="flex items-start gap-4" data-id="element-12641" data-mp="qggbok0z90ts">
                  <div className="flex-shrink-0" data-id="element-12642" data-mp="qevhwwmhllwz">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-12643" data-mp="v7ydfaatr5ft">
                      <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-12644" data-mp="105q5q5fdstkj"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-12645" data-mp="154b9ufxr7xe9">
                    <div className="flex items-center justify-between" data-id="element-12646" data-mp="1a4fpk3qu8o3f">
                      <div className="flex items-center gap-3" data-id="element-12647" data-mp="1a34my794pmdw">
                        <p className="text-sm font-medium text-gray-900" data-id="element-12648" data-mp="1e60wz9klsa68">
                          {row.posting}
                        </p>
                        {getStatusBadge(row.status.toLowerCase())}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-12649" data-mp="3fzqparb3lkmq">{row.effectiveAt}</p>
                    </div>
                  </div>
                </div>
              </div>)}
        </div>
      </div>

      {/* Refund Modal */}
      <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={sale.amount} transactionId={sale.transactionId} currency="MYR" availableBalance={availableBalance} acquirerPolicy={acquirerPolicy} onRefund={handleRefund} data-id="element-12650" data-mp="2yntnefina44r" />
    </div>;
}